import { css } from '@styled-system/css';
import { type FC } from 'react';

import { OptimizedBackgroundImage } from '@/shared/components/ui/OptimizedBackgroundImage';
import { ProductItemData } from '@/shared/types/product';
import { getItemConditionColor, getItemConditionLabel } from '@/shared/utils/analysis/itemCondition';
import { formatPrice } from '@/shared/utils/format';

interface ProductItemProps {
  product: ProductItemData;
}

export const ProductItem: FC<ProductItemProps> = ({ product }) => {
  const cardStyles = css({
    display: 'flex',
    flexDirection: 'row',
    gap: 'sm',
    alignItems: 'flex-end',
    bg: 'transparent',
    cursor: 'pointer',
    w: 'fit-content',
    h: 'fit-content',
    minW: 0,
    flexShrink: 0,
  });

  const photoSectionStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: 'xs',
    alignItems: 'flex-start',
    w: '6.8125rem',
    flexShrink: 0,
  });

  const mainImageStyles = css({
    w: '6.8125rem',
    h: '6.8125rem',
    bg: 'base.segBg',
    borderRadius: 'lg',
    flexShrink: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    position: 'relative',
  });

  const likeCountStyles = css({
    position: 'absolute',
    bottom: 'xxs',
    right: 'xs',
    display: 'flex',
    alignItems: 'center',
    gap: 'xxs',
    bg: 'base.segBg',
    borderRadius: 'xl',
    px: 'xs',
    py: 'xs',
    opacity: 0.95,
  });

  const heartIconStyles = css({
    w: '0.8rem',
    h: '0.8rem',
    flexShrink: 0,
    color: 'font.primary',
  });

  const likeCountTextStyles = css({
    fontFamily: 'Manrope, sans-serif',
    fontSize: 'xs',
    fontWeight: 600,
    color: 'font.primary',
    lineHeight: '1.07',
    pb: '0.025rem',
  });

  const miniPhotosContainerStyles = css({
    display: 'flex',
    flexDirection: 'row',
    gap: 'xs',
    alignItems: 'center',
    w: '100%',
  });

  const miniPhotoStyles = css({
    w: '3.25rem',
    h: '3.25rem',
    bg: 'base.segBg',
    borderRadius: 'lg',
    flexShrink: 0,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  });

  const contentSectionStyles = css({
    display: 'flex',
    flexDirection: 'column',
    h: '10.3125rem',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    w: '15.8125rem',
    flexShrink: 0,
  });

  const mainContentStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: 'md',
    alignItems: 'flex-start',
    w: '100%',
    flexShrink: 0,
  });

  const headerStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: 'xs',
    alignItems: 'flex-start',
    w: '100%',
    flexShrink: 0,
  });

  const titleStyles = css({
    color: 'font.secondary',
    fontFamily: 'Manrope, sans-serif',
    fontSize: 'xs',
    fontWeight: 500,
    lineHeight: '1.07',
    minWidth: '100%',
  });

  const priceTagRowStyles = css({
    display: 'flex',
    flexDirection: 'row',
    gap: 'xs',
    alignItems: 'flex-start',
    flexShrink: 0,
  });

  const priceStyles = css({
    color: 'font.primary',
    fontFamily: 'Manrope, sans-serif',
    fontSize: 'lg',
    fontWeight: 'bold',
    lineHeight: '1.07',
    flexShrink: 0,
  });

  const tagStyles = css({
    display: 'flex',
    flexDirection: 'row',
    gap: 'sm',
    alignItems: 'center',
    justifyContent: 'center',
    px: 'xs',
    py: '0.1875rem',
    borderRadius: 'xl',
    flexShrink: 0,
  });

  const tagTextStyles = css({
    fontFamily: 'Manrope, sans-serif',
    fontSize: 'xs',
    fontWeight: 600,
    lineHeight: '1.07',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  });

  const categoryProductStyles = css({
    display: 'flex',
    flexDirection: 'column',
    gap: 'xs',
    alignItems: 'flex-start',
    flexShrink: 0,
  });

  const productInfoStyles = css({
    display: 'flex',
    flexDirection: 'row',
    gap: 'xs',
    alignItems: 'center',
    w: '100%',
    flexShrink: 0,
  });

  const labelStyles = css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    color: 'font.lowkey',
    fontFamily: 'Manrope, sans-serif',
    fontSize: '2xs',
    fontWeight: 400,
    lineHeight: '1.07',
    w: '4rem',
    flexShrink: 0,
  });

  const valueStyles = css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    color: 'font.primary',
    fontFamily: 'Manrope, sans-serif',
    fontSize: '2xs',
    fontWeight: 600,
    lineHeight: '1.07',
    textAlign: 'center',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  });

  const sellContentStyles = css({
    display: 'flex',
    flexDirection: 'row',
    gap: 'sm',
    alignItems: 'center',
    color: 'font.lowkey',
    fontFamily: 'Manrope, sans-serif',
    fontSize: '2xs',
    fontWeight: 400,
    lineHeight: '1.07',
    textAlign: 'center',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleClick = () => {
    window.open(product.url, '_blank');
  };

  const getConditionBgColor = (condition: string) => {
    if (condition === 'veryGoodCondition') return 'rgba(7,113,43,0.07)';
    if (condition === 'goodCondition') return 'rgba(255,165,0,0.07)';
    if (condition === 'satisfactory') return 'rgba(255,69,0,0.07)';
    return 'rgba(7,113,43,0.07)';
  };

  return (
    <div className={cardStyles} onClick={handleClick}>
      {/* Section photo */}
      <div className={photoSectionStyles}>
        {/* Image principale */}
        <div className={mainImageStyles}>
          {product.images[0] && <OptimizedBackgroundImage src={product.images[0]} className={mainImageStyles} />}
          {/* Compteur de favoris */}
          {product.favouriteCount > 0 && (
            <div className={likeCountStyles}>
              <svg className={heartIconStyles} viewBox="0 0 10 11" fill="none">
                <path
                  d="M6.54167 2.16663C7.8625 2.16663 8.75 3.40829 8.75 4.56663C8.75 6.91246 5.06667 8.83329 5 8.83329C4.93333 8.83329 1.25 6.91246 1.25 4.56663C1.25 3.40829 2.1375 2.16663 3.45833 2.16663C4.21667 2.16663 4.7125 2.54579 5 2.87913C5.2875 2.54579 5.78333 2.16663 6.54167 2.16663Z"
                  stroke="#E6E6E9"
                  stroke-width="0.833333"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <span className={likeCountTextStyles}>{product.favouriteCount}</span>
            </div>
          )}
        </div>

        {/* Mini photos */}
        <div className={miniPhotosContainerStyles}>
          {Array.from({ length: 2 }, (_, index) => (
            <div key={`placeholder-${index}`} className={miniPhotoStyles}>
              {product.images[index + 1] && (
                <OptimizedBackgroundImage key={index + 1} src={product.images[index + 1]} className={miniPhotoStyles} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Section contenu */}
      <div className={contentSectionStyles}>
        <div className={mainContentStyles}>
          {/* Header */}
          <div className={headerStyles}>
            <div className={titleStyles}>{product.title}</div>
            <div className={priceTagRowStyles}>
              <div className={priceStyles}>{formatPrice(product.price)}</div>
              {product.itemCondition && (
                <div className={tagStyles} style={{ backgroundColor: getConditionBgColor(product.itemCondition) }}>
                  <div className={tagTextStyles} style={{ color: getItemConditionColor(product.itemCondition) }}>
                    {getItemConditionLabel(product.itemCondition)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Catégorie produit */}
          <div className={categoryProductStyles}>
            {product.attributes.map((attr, index) => (
              <div key={index} className={productInfoStyles}>
                <div className={labelStyles}>{attr.name}</div>
                <div className={valueStyles}>{attr.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Sell content */}
        <div className={sellContentStyles}>
          <div>Ajouté le {formatDate(product.uploadedAt)}</div>
          {product.soldAt && <div>Vendu le {formatDate(product.soldAt)}</div>}
        </div>
      </div>
    </div>
  );
};
